import { ChangeDetectorRef, Component, OnInit, AfterViewInit, ElementRef, ViewChild, Input, HostListener, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { TemperatureService } from '../temperature.service';
import { v4 as uuidv4 } from 'uuid'; 
import { HistoryService } from '../history.service';
import { marked } from 'marked';
import { markedHighlight } from 'marked-highlight';
import hljs from 'highlight.js';
import { OutageService } from '../outage.service';
import { ModeService, WritingMode } from '../mode.service';
import { ConversationMode } from '../history.service';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { TopbarComponent } from '../topbar/topbar.component';
import { UploadComponent } from '../upload/upload.component';
import { ToggleComponent } from '../toggle/toggle.component';
import { UploadcontentComponent } from '../uploadcontent/uploadcontent.component';

@Component({
  selector: 'app-generalquestions',
  templateUrl: './generalquestions.component.html',
  styleUrls: ['./generalquestions.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    SidebarComponent,
    TopbarComponent,
    UploadComponent,
    ToggleComponent,
    UploadcontentComponent
  ]
})
export class GeneralquestionsComponent implements OnInit, AfterViewInit {
  chatForm!: FormGroup;
  context: string = "";
  window = window;
  chatMessages: {
    sender: string;
    text: SafeHtml;
    isTyping?: boolean;
    files?: { name: string }[];
    /** Data-URI of an image the user attached/pasted, shown inline in the bubble. */
    imageUrl?: string;
    /** Data-URI of a chart PNG returned by the backend, shown inline in a bot bubble. */
    chartImageUrl?: string;
    /** A non-image file the user uploaded, shown as a clickable (re-downloadable) chip. */
    fileAttachment?: { name: string; dataUri: string };
    sources?: string[];
    confidence?: 'high' | 'medium' | 'low' | 'none';
    // raw text kept for feedback submission
    rawText?: string;
    // feedback: null=not voted, 'up'=thumbs up, 'down'=thumbs down
    feedback?: 'up' | 'down' | null;
    // the user question that triggered this bot message (for feedback)
    question?: string;
    // true once the final markdown has been parsed & rendered (stops fade loops)
    rendered?: boolean;
  }[] = [];

  /** AbortController for the active SSE stream */
  private activeStreamController?: AbortController;
  
  private welcomeMessages: string[] = [
    "I can help you write proposals",
    "I can help you write code",
    "I can help you write documentation",
    "I can help you write reports",
    "I can help you write analysis"
  ];

  currentWelcomeMessage: string = '';
  typewriterText: string = '';
  typewriterWords: string[] = [];
  currentWordIndex: number = 0;
  isTyping: boolean = false;
  private typewriterInterval: any;

  getRandomWelcomeMessage(): string {
    // Mode-specific static welcome — don't rotate the generic "write code/proposals"
    // messages while in Code or Writing mode (that was confusing).
    const mode = this.modeService.getAppMode();
    if (mode === 'code') {
      if (this.typewriterInterval) { clearInterval(this.typewriterInterval); this.typewriterInterval = null; }
      return 'What can I help you build?';
    }
    if (mode === 'writing') {
      if (this.typewriterInterval) { clearInterval(this.typewriterInterval); this.typewriterInterval = null; }
      return this.writingMode === 'generate'
        ? 'What should I write?'
        : this.writingMode === 'review'
          ? 'Paste a document to review'
          : 'Paste a document to rewrite';
    }
    if (mode === 'image') {
      if (this.typewriterInterval) { clearInterval(this.typewriterInterval); this.typewriterInterval = null; }
      return 'Describe an image to generate';
    }

    if (window.innerWidth <= 768) {
      // At 768px or below, return a static message without animation
      return "Welcome";
    }

    if (!this.currentWelcomeMessage) {
      // Select random message
      const randomIndex = Math.floor(Math.random() * this.welcomeMessages.length);
      this.currentWelcomeMessage = this.welcomeMessages[randomIndex];
      
      // Split message into base and typewriter parts
      const words = this.currentWelcomeMessage.split(' ');
      const baseWords = words.slice(0, 5).join(' '); // "I can help you write"
      this.typewriterWords = [words.slice(5).join(' ')]; // The last part (proposals, code, etc.)
      this.typewriterText = baseWords;
      this.currentWordIndex = 0;
      
      // Start typewriter effect
      this.startTypewriterEffect();
    }
    return this.typewriterText;
  }

  private startTypewriterEffect(): void {
    if (this.typewriterInterval) {
      clearInterval(this.typewriterInterval);
    }

    this.isTyping = true;
    let currentWord = '';
    let currentCharIndex = 0;
    let isDeleting = false;
    let currentMessageIndex = 0;

    this.typewriterInterval = setInterval(() => {
      if (this.currentWordIndex >= this.typewriterWords.length) {
        // Move to next message when current one is complete
        currentMessageIndex = (currentMessageIndex + 1) % this.welcomeMessages.length;
        const nextMessage = this.welcomeMessages[currentMessageIndex];
        const words = nextMessage.split(' ');
        this.typewriterWords = [words.slice(5).join(' ')]; // The last part
        this.typewriterText = words.slice(0, 5).join(' '); // "I can help you write"
        this.currentWordIndex = 0;
        currentCharIndex = 0;
        isDeleting = false;
        return;
      }

      const targetWord = this.typewriterWords[this.currentWordIndex];

      if (isDeleting) {
        currentWord = targetWord.substring(0, currentCharIndex - 1);
        currentCharIndex--;
      } else {
        currentWord = targetWord.substring(0, currentCharIndex + 1);
        currentCharIndex++;
      }

      const baseText = "I can help you write";
      this.typewriterText = baseText + ' ' + currentWord;
      this.cdr.detectChanges();

      if (!isDeleting && currentCharIndex === targetWord.length) {
        setTimeout(() => {
          isDeleting = true;
        }, 1000); // Pause before deleting
      } else if (isDeleting && currentCharIndex === 0) {
        isDeleting = false;
        this.currentWordIndex++;
        currentCharIndex = 0;
      }
    }, 100); // Typing speed
  }

  @ViewChild('messagesContainer', { static: false }) messagesContainer!: ElementRef;
  @ViewChild('chatInputContainer', { static: false }) chatInputContainer!: ElementRef<HTMLDivElement>;

  @Input() uploadUrl: string = '';

  // Add method to check if chat input is centered
  isChatInputCentered(): boolean {
    return this.chatInputContainer?.nativeElement?.classList?.contains('centered') || false;
  }

  isSidebarOpen: boolean = false;
  isStopVisible: boolean = false;
  selectedFiles: File[] = [];
  currentComponentUrl: string = '';
  hasOverflow: boolean = false;

  private currentChatId: string | null = null;

  /**
   * When the user switches to code/writing from a chat that already has content,
   * we open a fresh empty chat IN that mode. The navigation to chatId:null fires
   * the queryParams handler; this flag tells that handler to keep the chosen mode
   * instead of resetting to general.
   */
  private pendingModeForNewChat: 'standard' | 'code' | 'writing' | 'image' | null = null;

  selectedMode: string = 'standard';

  // --- Writing mode state ---
  /** Writing sub-mode: review | rewrite | generate. */
  writingMode: WritingMode = 'generate';
  /** Inline hint shown under the document pane (e.g. "Paste some text to review"). */
  writingHint: string = '';

  // --- Attachment state (image paste / file picker, all modes) ---
  /** Pasted/selected image, staged for the vision endpoint. */
  attachedImage: { dataUri: string; mimetype: string } | null = null;
  /** Non-image file staged for the ingest endpoint. */
  attachedFile: File | null = null;
  /** Data URI of the staged non-image file (for showing/re-downloading in the chat). */
  attachedFileDataUri: string | null = null;

  /** When set, shows a full-screen lightbox of this image URL. */
  lightboxImageUrl: string | null = null;

  /** Open the image lightbox (click an image in the chat). */
  openLightbox(url: string): void {
    this.lightboxImageUrl = url;
    this.cdr.detectChanges();
  }

  /** Close the image lightbox. */
  closeLightbox(): void {
    this.lightboxImageUrl = null;
    this.cdr.detectChanges();
  }

  // --- Prohibited words (client-side highlighter, no API) ---
  /** FI style guide prohibited words — flagged live as the user types in writing mode. */
  private readonly prohibitedWords: string[] = [
    'all', 'any', 'every', 'comprehensive', 'full', 'always', 'never',
    'assure', 'ensure', 'guarantee', 'warrant', 'best', 'greatest', 'latest',
    'determine', 'select', 'expert', 'specialist', 'implement', 'partner',
    'partnership', 'alliance', 'satisfy', 'unique', 'unparalleled',
    'world class', 'cutting edge', 'state of the art', 'engagement'
  ];
  /** Prohibited words currently detected in the input (shown as chips). */
  flaggedWords: string[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private historyService: HistoryService,
    private fb: FormBuilder,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private temperatureService: TemperatureService,
    private cdr: ChangeDetectorRef,
    private outageService: OutageService,
    private modeService: ModeService
  ) { 
  }
  
  private botSubscription?: Subscription;
  private routeSubscription?: Subscription;
  private historySubscription?: Subscription;


  ngOnInit(): void {
    // Initialize sidebar state from localStorage
    this.isSidebarOpen = this.getInitialSidebarState();
    
    // Configure marked once — disable unused features for faster parsing
    marked.setOptions({ mangle: false, headerIds: false } as any);

    // Use marked-highlight plugin
    marked.use(
      markedHighlight({
        highlight(code, lang) {
          if (lang && hljs.getLanguage(lang)) {
            return hljs.highlight(code, { language: lang }).value;
          }
          return hljs.highlightAuto(code).value;
        }
      })
    );
  
    this.chatForm = this.fb.group({
      message: ['', [Validators.required, Validators.minLength(1)]],
    });
  
    this.outageService.checkServerStatus();
  
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      if (params['chatId']) {
        // Don't reload if we're already on this chat (e.g. after stream done sets the URL)
        if (params['chatId'] === this.currentChatId) return;

        this.cancelBotResponse();
        // Hard reset ALL mode/input state before loading, so nothing bleeds across chats.
        this.resetModeState();
        this.currentChatId = params['chatId'];
        const existingChat = this.historyService.getConversations().find(chat => chat.id === this.currentChatId);

        // Restore the mode this chat was created in (per-chat attribute).
        const restoredMode = this.historyService.getConversationMode(this.currentChatId!);
        this.selectedMode = restoredMode;
        this.modeService.setMode(restoredMode);

        if (existingChat) {
          Promise.all(existingChat.messages.map(async (msg) => {
            const htmlWithCopyButtons = await this.safeParse(msg.text.trim());
            const storedImg = (msg as any).imageUrl;
            return {
              sender: msg.sender,
              text: this.sanitizer.bypassSecurityTrustHtml(htmlWithCopyButtons),
              files: msg.files?.map(file => ({ name: file.name })) || [],
              // A stored image on a user message is their attachment; on a bot
              // message it's a rendered chart.
              imageUrl: msg.sender === 'user' ? storedImg : undefined,
              chartImageUrl: msg.sender === 'bot' ? storedImg : undefined,
              fileAttachment: (msg as any).fileAttachment
            };
          })).then((messages) => {
            this.chatMessages = messages;
            this.cdr.detectChanges();
          });
        }
      } else {
        // No chatId → fresh/empty chat. Force a clean state so a previous
        // writing/code session doesn't bleed into the new chat.
        this.resetModeState();
        // If this new chat was intentionally opened in a mode (switching surfaces
        // from a chat with content), apply that chosen mode; otherwise stay general.
        if (this.pendingModeForNewChat) {
          this.selectedMode = this.pendingModeForNewChat;
          this.modeService.setMode(this.pendingModeForNewChat);
          this.pendingModeForNewChat = null;
        }
        this.currentChatId = null;
        this.chatMessages = [];
      }
      this.cdr.detectChanges();
    });
  
    this.historySubscription = this.historyService.historyUpdated.subscribe(() => this.cdr.detectChanges());

    // Make handleCopyClick available globally
    (window as any).handleCopyClick = (event: MouseEvent) => {
      this.handleCopyClick(event);
    };
  }
  

ngOnDestroy(): void {
  this.routeSubscription?.unsubscribe();
  this.cancelBotResponse(); // Stop any ongoing bot response
  
  if (this.historySubscription) {
    this.historySubscription.unsubscribe();
    this.historySubscription = undefined; // Explicitly set to undefined
  }

  if (this.botSubscription) {
    this.botSubscription.unsubscribe();
    this.botSubscription = undefined;
  }

  this.activeStreamController?.abort();

  if (this.typewriterInterval) {
    clearInterval(this.typewriterInterval);
  }
}


handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    if (this.botIsTyping) return;
    this.sendMessage();
  }
}

updateContext(newContext: string): void {
  this.context = newContext; // Store the updated context
}


  ngAfterViewInit(): void {
    const observer = new MutationObserver(() => {
      this.checkOverflow();
    });
  
    observer.observe(this.chatInputContainer.nativeElement, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  
    // Initialize overflow detection immediately
    this.checkOverflow();
  
    // Observe changes that may trigger overflow
    const resizeObserver = new ResizeObserver(() => {
      this.checkOverflow();
    });
    resizeObserver.observe(this.messagesContainer.nativeElement);

    // Add scroll event listener to track scroll position
    this.messagesContainer.nativeElement.addEventListener('scroll', () => {
      this.checkOverflow();
    });
  }
  
  // Detects overflow explicitly:
  checkOverflow(): void {
    const el = this.messagesContainer.nativeElement;
    // Only show shadow if user has scrolled down
    const isScrolled = el.scrollTop > 0;
    if (this.hasOverflow !== isScrolled) {
      this.hasOverflow = isScrolled;
      this.cdr.detectChanges();
    }
  }
  
  // Check also on window resize (for safety)
  @HostListener('window:resize')
  onResize(): void {
    this.checkOverflow();
  }  

  /** Toggles the sidebar */
  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
    this.updateSidebarState();
  }

  private getInitialSidebarState(): boolean {
    const savedState = localStorage.getItem('sidebarOpen');
    return savedState ? JSON.parse(savedState) : false; // Default to closed
  }

  private updateSidebarState(): void {
    localStorage.setItem('sidebarOpen', JSON.stringify(this.isSidebarOpen));
  }

  /** Clears all chat messages */
  clearChat(): void {
    this.chatMessages = [];
  }

  getTemperature(): number {
    return this.temperatureService.getDefaultTemperature('generalQuestions');
  }

  /** Temperature slider changed (from the toggle component in writing/code mode). */
  onTemperatureChanged(value: number): void {
    this.temperatureService.setTemperature('generalQuestions', value);
  }

  onFilesSelected(files: File[]) {
    this.selectedFiles = [...this.selectedFiles, ...files]; // append new files instead of replacing
    this.cdr.detectChanges();
  }
  
  onFileRemoved(index: number) {
    this.selectedFiles.splice(index, 1); // Remove specific file
  }
  
/** Sends a user message and fetches response from API */
async sendMessage(): Promise<void> {
  if (this.botIsTyping) {
    return; // Prevent sending a new message while bot is responding or animating
  }

  const rawMessage = this.chatForm.get('message')?.value;
  const appMode = this.modeService.getAppMode();

  const hasMessage = !!rawMessage && !!rawMessage.trim();
  const hasAttachment = !!this.attachedImage || !!this.attachedFile || this.selectedFiles.length > 0;

  // Nothing to send.
  if (!hasMessage && !hasAttachment) return;
  this.writingHint = '';

  const userMessage = hasMessage ? rawMessage : this.defaultPromptForMode(appMode);

  this.isStopVisible = true;
  this.botIsTyping = true;

  const markdownHtml = await marked.parse(userMessage.trim());
  const filesToSave = this.selectedFiles.map(file => ({ name: file.name }));

  // Store files in history (include any pasted image / uploaded file so it persists)
  const imageForHistory = this.attachedImage?.dataUri;
  const fileForHistory = (this.attachedFile && this.attachedFileDataUri)
    ? { name: this.attachedFile.name, dataUri: this.attachedFileDataUri }
    : undefined;
  if (!this.currentChatId) {
    this.currentChatId = uuidv4();
    this.historyService.saveConversation(this.currentChatId, userMessage, appMode as ConversationMode);
    this.historyService.appendMessage(this.currentChatId, userMessage, 'user', filesToSave, imageForHistory, fileForHistory);
    this.historyService.historyUpdated.next();
  } else {
    this.historyService.appendMessage(this.currentChatId, userMessage, 'user', filesToSave, imageForHistory, fileForHistory);
  }

  // Capture a pasted/attached image so it stays visible in the chat bubble.
  const pastedImageUrl = this.attachedImage?.dataUri;
  // Capture an uploaded (non-image) file so it shows as a clickable chip in the chat.
  const fileAttachment = (this.attachedFile && this.attachedFileDataUri)
    ? { name: this.attachedFile.name, dataUri: this.attachedFileDataUri }
    : undefined;

  // Add user message to chat
  this.chatMessages.push({
    sender: 'user',
    text: this.sanitizer.bypassSecurityTrustHtml(markdownHtml),
    files: this.selectedFiles.map(file => ({ name: file.name })),
    imageUrl: pastedImageUrl,
    fileAttachment
  });
  
  this.chatForm.reset();
  this.resetTextareaHeight();
  this.cdr.detectChanges(); 
  this.scrollToBottom();

  const temperature = this.getTemperature();

  if (this.botSubscription) {
    this.botSubscription.unsubscribe();
    this.botSubscription = undefined;
  }

  // Show typing indicator
  this.chatMessages.push({ sender: 'bot', text: '', isTyping: true });
  this.cdr.detectChanges();

  // Attachments take priority across ALL modes.
  if (this.attachedImage) {
    // Pasted/selected image → vision endpoint.
    this.sendVisionMessage(userMessage, temperature);
  } else if (this.attachedFile) {
    // Non-image file → universal ingest endpoint.
    this.sendIngestMessage(userMessage);
  } else if (appMode === 'image') {
    // Image mode: every prompt generates a picture.
    this.sendImageGenMessage(userMessage);
  } else if (appMode === 'code') {
    this.sendCodeMessage(userMessage, temperature);
  } else if (appMode === 'writing') {
    this.sendWritingMessage(userMessage, temperature);
  } else if (this.selectedFiles.length > 0) {
    this.sendDocumentRagMessage(userMessage);
  } else if (appMode === 'standard' && this.looksLikeImageGenRequest(userMessage)) {
    // "Draw / generate a picture of X" → backend image-generation endpoint.
    this.sendImageGenMessage(userMessage);
  } else if (appMode === 'standard' && this.looksLikeChartRequest(userMessage)) {
    // Chart/graph request → backend chart renderer (returns a real PNG).
    this.sendChartMessage(userMessage);
  } else {
    // Text-only: use SSE streaming endpoint
    const streamUrl = this.modeService.getStreamEndpoint();
    const payload = {
      question: userMessage,
      temperature,
      context: this.context || '',
      uuid: this.currentChatId || ''
    };
    this.sendMessageStream(streamUrl, payload, userMessage);
  }

  this.selectedFiles = [];
  this.attachedImage = null;
  this.attachedFile = null;
  this.attachedFileDataUri = null;
  this.flaggedWords = [];
  this.cdr.detectChanges();
}

/** Default chat prompt when the user submits a document/code with no typed question. */
private defaultPromptForMode(appMode: 'standard' | 'code' | 'writing' | 'image'): string {
  if (appMode === 'writing') {
    return this.writingMode === 'review'
      ? 'Please review this document.'
      : this.writingMode === 'rewrite'
        ? 'Please rewrite this document to FI style.'
        : '';
  }
  if (appMode === 'code') {
    return 'Please review this code.';
  }
  return '';
}

/** Pull a human-readable message out of an HttpErrorResponse (surfaces backend 400s). */
private serverErrorMessage(error: any, fallback: string): string {
  const body = error?.error;
  if (body && typeof body === 'object' && typeof body.message === 'string' && body.message.trim()) {
    return body.message;
  }
  if (typeof body === 'string' && body.trim()) {
    return body;
  }
  return fallback;
}

/** Shared handler for non-streaming JSON/multipart POST responses. */
private handleStandardResponse(response: { message: string; status: string; sources?: string[]; confidence?: 'high' | 'medium' | 'low' | 'none' }, userMessage: string): void {
  this.chatMessages = this.chatMessages.filter(msg => !msg.isTyping);
  if (response.status === 'success' && response.message) {
    this.animateBotTyping(response.message, response.sources ?? [], response.confidence, userMessage, () => {
      this.botIsTyping = false;
      this.isStopVisible = false;
      this.cdr.detectChanges();
    });
  } else {
    this.showBotError('Invalid response from server');
  }
}

/** POST /questions/documentrag — general chat with file attachments. */
private sendDocumentRagMessage(userMessage: string): void {
  const apiUrl = this.modeService.getEndpoint(true);
  const formData = new FormData();
  formData.append('question', userMessage);
  formData.append('context', this.context || '');
  formData.append('temperature', this.getTemperature().toString());
  formData.append('uuid', this.currentChatId || '');

  this.selectedFiles.forEach((file) => {
    formData.append('files', file, file.name);
  });

  this.botSubscription = this.http
    .post<{ message: string; status: string; sources?: string[]; confidence?: 'high' | 'medium' | 'low' | 'none' }>(apiUrl, formData)
    .subscribe(
      (response) => this.handleStandardResponse(response, userMessage),
      (error) => {
        console.error('Error fetching bot response:', error);
        this.chatMessages = this.chatMessages.filter(msg => !msg.isTyping);
        this.showBotError('Error retrieving response');
      }
    );
}

/** POST /questions/code — body { question, uuid, temperature }. Code mode is a plain chat. */
private sendCodeMessage(userMessage: string, temperature: number): void {
  const payload = {
    question: userMessage,
    uuid: this.currentChatId || '',
    temperature
  };

  this.botSubscription = this.http
    .post<{ message: string; status: string; sources?: string[]; confidence?: 'high' | 'medium' | 'low' | 'none' }>(
      this.modeService.getCodeEndpoint(),
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    )
    .subscribe(
      (response) => this.handleStandardResponse(response, userMessage),
      (error) => {
        console.error('Error fetching code response:', error);
        this.chatMessages = this.chatMessages.filter(msg => !msg.isTyping);
        this.showBotError('Error retrieving response');
      }
    );
}

/**
 * Writing assistant.
 * - File present: POST /questions/write/upload (multipart: file, question, uuid, mode)
 * - Otherwise:    POST /questions/write (JSON: question, uuid, document_text, mode)
 * review/rewrite require document_text or a file; generate works from the question alone.
 */
/**
 * Writing assistant. The user types directly in the main chat input; the active
 * tab (review/rewrite/generate) is the mode. For review/rewrite the backend
 * requires document_text, so we send the typed text as both question and
 * document_text.
 */
private sendWritingMessage(userMessage: string, temperature: number): void {
  const mode = this.writingMode;
  const payload: {
    question: string; uuid: string; mode: WritingMode; temperature: number; document_text?: string;
  } = {
    question: userMessage,
    uuid: this.currentChatId || '',
    mode,
    temperature
  };
  if (mode === 'review' || mode === 'rewrite') {
    payload.document_text = userMessage;
  }

  this.botSubscription = this.http
    .post<{ message: string; status: string; sources?: string[]; confidence?: 'high' | 'medium' | 'low' | 'none' }>(
      this.modeService.getWriteEndpoint(),
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    )
    .subscribe(
      (response) => this.handleStandardResponse(response, userMessage),
      (error) => {
        console.error('Error fetching writing response:', error);
        this.chatMessages = this.chatMessages.filter(msg => !msg.isTyping);
        this.showBotError(this.serverErrorMessage(error, 'Error retrieving response'));
      }
    );
}

/**
 * Heuristic: does the user's message ask for a chart/graph/plot/visualization?
 * Used (in general mode only) to route to the backend chart renderer instead of
 * the text chat, so they get a real image rather than ASCII art.
 */
private looksLikeChartRequest(text: string): boolean {
  const t = text.toLowerCase();
  // Must reference a visual artifact AND an intent to produce/show one.
  const chartNoun = /\b(chart|graph|plot|bar chart|line chart|pie chart|scatter|histogram|visuali[sz]ation|visuali[sz]e)\b/.test(t);
  const makeVerb = /\b(make|create|generate|draw|plot|show|give|build|chart|graph|visuali[sz]e)\b/.test(t);
  return chartNoun && makeVerb;
}

/**
 * Heuristic: does the user want an actual generated picture/illustration
 * (not a data chart)? e.g. "draw a clown", "generate an image of a city skyline".
 */
private looksLikeImageGenRequest(text: string): boolean {
  const t = text.toLowerCase();
  const imageNoun = /\b(picture|image|photo|illustration|drawing|artwork|logo|icon|portrait|poster|clip ?art|sketch)\b/.test(t);
  const makeVerb = /\b(draw|generate|create|make|design|paint|sketch|render|produce|show me)\b/.test(t);
  // Exclude data-viz words so charts still go to the chart renderer.
  const isCharty = /\b(chart|graph|plot|bar|pie|line|scatter|histogram)\b/.test(t);
  return imageNoun && makeVerb && !isCharty;
}

/**
 * POST /questions/image — body { prompt, uuid }. Renders the generated PNG inline.
 * Reuses the chart message renderer (same inline image + copy/download + lightbox).
 */
private sendImageGenMessage(userMessage: string): void {
  const payload = { prompt: userMessage, uuid: this.currentChatId || '' };

  this.botSubscription = this.http
    .post<{ status: string; image_b64?: string; message?: string }>(
      this.modeService.getImageGenEndpoint(),
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    )
    .subscribe(
      (response) => {
        this.chatMessages = this.chatMessages.filter(msg => !msg.isTyping);
        if (response.status === 'success' && response.image_b64) {
          this.renderChartMessage(response.image_b64, response.message || '', userMessage);
        } else if (response.message) {
          this.animateBotTyping(response.message, [], undefined, userMessage, () => {
            this.botIsTyping = false; this.isStopVisible = false; this.cdr.detectChanges();
          });
        } else {
          this.showBotError('Could not generate an image for that request.');
        }
      },
      (error) => {
        console.error('Error generating image:', error);
        this.chatMessages = this.chatMessages.filter(msg => !msg.isTyping);
        this.showBotError(this.serverErrorMessage(error, 'Image generation isn\u2019t available yet.'));
      }
    );
}

/** POST /questions/chart — body { question, uuid }. Renders a bot bubble with the returned PNG. */
private sendChartMessage(userMessage: string): void {
  const payload = {
    question: userMessage,
    uuid: this.currentChatId || ''
  };

  this.botSubscription = this.http
    .post<{ message?: string; status: string; chart_b64?: string; chart_spec?: any }>(
      this.modeService.getChartEndpoint(),
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    )
    .subscribe(
      (response) => {
        this.chatMessages = this.chatMessages.filter(msg => !msg.isTyping);
        if (response.status === 'success' && response.chart_b64) {
          const caption = response.message || '';
          this.renderChartMessage(response.chart_b64, caption, userMessage);
        } else if (response.message) {
          // No chart came back but there's text — show it normally.
          this.animateBotTyping(response.message, [], undefined, userMessage, () => {
            this.botIsTyping = false; this.isStopVisible = false; this.cdr.detectChanges();
          });
        } else {
          this.showBotError('Could not generate a chart for that request.');
        }
      },
      (error) => {
        console.error('Error generating chart:', error);
        this.chatMessages = this.chatMessages.filter(msg => !msg.isTyping);
        // Fall back to a normal text answer so the user still gets something.
        this.showBotError(this.serverErrorMessage(error, 'Could not generate a chart. Try rephrasing.'));
      }
    );
}

/** Push a bot message that shows a rendered chart image (+ optional caption). */
private async renderChartMessage(chartB64: string, caption: string, userQuestion: string): Promise<void> {
  const captionHtml = caption ? await this.safeParse(caption) : '';
  this.chatMessages.push({
    sender: 'bot',
    text: this.sanitizer.bypassSecurityTrustHtml(captionHtml),
    chartImageUrl: chartB64,
    rawText: caption,
    feedback: null,
    question: userQuestion,
    rendered: true
  });
  // Persist the chart in history so it survives reloads/switches.
  if (this.currentChatId) {
    this.historyService.appendMessage(this.currentChatId, caption, 'bot', undefined, chartB64);
  }
  this.botIsTyping = false;
  this.isStopVisible = false;
  this.cdr.detectChanges();
  this.scrollToBottom();
}

/** POST /questions/vision — body { question, uuid, image_b64, mimetype, temperature } */
private sendVisionMessage(userMessage: string, temperature: number): void {
  if (!this.attachedImage) return;
  const payload = {
    question: userMessage,
    uuid: this.currentChatId || '',
    image_b64: this.attachedImage.dataUri,
    mimetype: this.attachedImage.mimetype,
    temperature
  };

  this.botSubscription = this.http
    .post<{ message: string; status: string; sources?: string[]; confidence?: 'high' | 'medium' | 'low' | 'none' }>(
      this.modeService.getVisionEndpoint(),
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    )
    .subscribe(
      (response) => this.handleStandardResponse(response, userMessage),
      (error) => {
        console.error('Error fetching vision response:', error);
        this.chatMessages = this.chatMessages.filter(msg => !msg.isTyping);
        this.showBotError(this.serverErrorMessage(error, 'Error analyzing image'));
      }
    );
}

/** POST /questions/ingest — multipart { file, uuid, question } */
private sendIngestMessage(userMessage: string): void {
  if (!this.attachedFile) return;
  const formData = new FormData();
  formData.append('file', this.attachedFile, this.attachedFile.name);
  formData.append('uuid', this.currentChatId || '');
  formData.append('question', userMessage);

  this.botSubscription = this.http
    .post<{ message: string; status: string; sources?: string[]; confidence?: 'high' | 'medium' | 'low' | 'none'; extracted_text?: string }>(
      this.modeService.getIngestEndpoint(),
      formData
    )
    .subscribe(
      (response) => this.handleStandardResponse(response, userMessage),
      (error) => {
        console.error('Error ingesting file:', error);
        this.chatMessages = this.chatMessages.filter(msg => !msg.isTyping);
        this.showBotError(this.serverErrorMessage(error, 'Error processing file'));
      }
    );
}

/** Show a red error message as a bot bubble */
private showBotError(msg: string): void {
  this.chatMessages = this.chatMessages.filter(m => !m.isTyping);
  this.chatMessages.push({
    sender: 'bot',
    text: this.sanitizer.bypassSecurityTrustHtml(`<span style="color: red;">${msg}</span>`)
  });
  this.botIsTyping = false;
  this.isStopVisible = false;
  this.cdr.detectChanges();
}

/**
 * Reads the /questions/stream SSE endpoint via fetch + ReadableStream.
 * Tokens arrive immediately so the first word appears in ~1s.
 * Protocol:
 *   data: {"type":"meta","sources":[...],"confidence":"high"}
 *   data: {"type":"token","text":"..."}   (many)
 *   data: {"type":"done","message":"<full>"}
 *   data: {"type":"error","message":"..."}
 */
private async sendMessageStream(url: string, payload: object, userQuestion: string): Promise<void> {
  this.activeStreamController?.abort();
  this.activeStreamController = new AbortController();
  const { signal } = this.activeStreamController;

  // Keep the isTyping bubble (pulsing ball) while we wait for the stream
  // — the botMsgIndex slot was already pushed as isTyping:true by the caller
  const botMsgIndex = this.chatMessages.length - 1;
  this.chatMessages[botMsgIndex] = {
    sender: 'bot',
    text: this.sanitizer.bypassSecurityTrustHtml(''),
    sources: [],
    confidence: undefined,
    feedback: null,
    question: userQuestion,
    isTyping: true
  };
  this.cdr.detectChanges();

  let accumulatedText = '';
  let streamSources: string[] = [];
  let streamConfidence: 'high' | 'medium' | 'low' | 'none' | undefined;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal
    });

    if (!response.ok || !response.body) {
      throw new Error(`HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE lines are separated by "\n\n"; split on any newlines
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? ''; // keep incomplete last line

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const jsonStr = trimmed.slice(5).trim();
        if (!jsonStr) continue;

        let event: any;
        try { event = JSON.parse(jsonStr); } catch { continue; }

        if (event.type === 'meta') {
          streamSources = event.sources ?? [];
          streamConfidence = event.confidence ?? undefined;
        } else if (event.type === 'token') {
          accumulatedText += event.text;
        } else if (event.type === 'done') {
          const fullText = (event.message && event.message.trim())
            ? event.message
            : accumulatedText;
          if (!fullText.trim()) break;

          // Remove the isTyping bubble — animateBotTyping will push its own slot
          this.chatMessages.splice(botMsgIndex, 1);
          this.cdr.detectChanges();

          // Hand the full text to the original word-chunk + fade-in animator
          const cleaned = this.embedSources(fullText);
          this.animateBotTyping(cleaned, streamSources, streamConfidence, userQuestion, () => {
            this.botIsTyping = false;
            this.isStopVisible = false;
            this.cdr.detectChanges();
          });

          if (this.currentChatId) {
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { chatId: this.currentChatId },
              queryParamsHandling: 'merge'
            });
          }
          break;
        } else if (event.type === 'error') {
          throw new Error(event.message ?? 'Stream error');
        }
      }
    }
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      // User stopped — remove the isTyping bubble
      if (this.chatMessages[botMsgIndex]?.sender === 'bot') {
        this.chatMessages.splice(botMsgIndex, 1);
      }
    } else {
      console.error('Stream error:', err);
      if (this.chatMessages[botMsgIndex]) {
        this.chatMessages[botMsgIndex].isTyping = false;
        this.chatMessages[botMsgIndex].text = this.sanitizer.bypassSecurityTrustHtml(
          '<span style="color: red;">Error retrieving response</span>'
        );
      }
    }
    // Only reset typing state here — the success path lets animateBotTyping own it
    this.botIsTyping = false;
    this.isStopVisible = false;
    this.cdr.detectChanges();
    this.scrollToBottom();
  } finally {
    this.activeStreamController = undefined;
  }
}

/**
 * Submit thumbs-up / thumbs-down feedback to POST /feedback.
 * Idempotent: clicking the same rating again un-rates.
 */
submitFeedback(msgIndex: number, rating: 'up' | 'down'): void {
  const msg = this.chatMessages[msgIndex];
  if (!msg || msg.sender !== 'bot') return;

  // Toggle off if same rating clicked twice
  const newRating = msg.feedback === rating ? null : rating;
  msg.feedback = newRating;
  this.cdr.detectChanges();

  if (!newRating) return; // un-rated, nothing to POST

  const feedbackPayload = {
    uuid: this.currentChatId ?? '',
    question: msg.question ?? '',
    answer: msg.rawText ?? '',
    rating: newRating
  };

  this.http.post(this.modeService.getFeedbackEndpoint(), feedbackPayload, {
    headers: { 'Content-Type': 'application/json' }
  }).subscribe({
    error: (err) => console.error('Feedback submission failed:', err)
  });
}


stopBotResponse(): void {
  if (this.botSubscription) {
    this.botSubscription.unsubscribe();
    this.botSubscription = undefined;
  }

  this.activeStreamController?.abort();
  this.activeStreamController = undefined;

  for (let i = this.chatMessages.length - 1; i >= 0; i--) {
    if (this.chatMessages[i].sender === 'bot') {
      this.chatMessages.splice(i, 1); // Remove only the latest bot message
      break;
    }
  }

  this.botIsTyping = false;
  this.isStopVisible = false;

  this.cdr.detectChanges();
}

cancelBotResponse(): void {
  if (this.botSubscription) {
    this.botSubscription.unsubscribe();
    this.botSubscription = undefined;
  }

  this.activeStreamController?.abort();
  this.activeStreamController = undefined;

  this.chatMessages = []; // Clear the chat UI
  this.botIsTyping = false;
  this.isStopVisible = false; // Hide stop button
  this.chatForm.reset(); // Reset input field

  this.cdr.detectChanges();
}

onNewChat(): void {
  this.cancelBotResponse(); // Stop bot responses
  this.currentChatId = null; // Reset current chat ID
  this.chatMessages = []; // Clear messages
  this.isStopVisible = false; // Hide stop button
  this.currentWelcomeMessage = ''; // Reset welcome message

  // Full hard reset — New Chat always starts clean in general mode.
  this.resetModeState();

  this.resetTextareaHeight(); // Reset textarea height
  this.resetInputWrapperHeight(); // Reset input wrapper height

  this.historyService.historyUpdated.next(); // Notify sidebar to refresh

  // Remove `chatId` from URL using Angular Router
  this.router.navigate([], {
    relativeTo: this.route,
    queryParams: { chatId: null }, // Set chatId to null
    queryParamsHandling: 'merge' // Keeps other query params unchanged
  });
}

onChatSelected(chatId: string): void {
  this.cancelBotResponse(); // Stop any ongoing bot response
  this.context = ""; // Reset context

  this.router.navigate([], {
    queryParams: { chatId: chatId },
    queryParamsHandling: 'merge'
  });
}

botIsTyping: boolean = false;

// Trim Dates from Source File Names
removeDates(filename: string): string {
  // Remove extraneous dates, version numbers, and .pdf extension off the end of file names
  const newName = filename.replace(/[_-]?(v\d+|\d{8}|\d{4}-\d{2}-\d{2}|\d{2}-\d{2}-\d{4}|\(\d+\)|_v\d+\.\d+|_\d+\.\d+|_\d+)(?=\.\w{3,4}$)/i, "")
                          .replace('.pdf', '')
                          .trim();
  return newName;
}

// Response Sources Extractor — strips inline "Sources:" section from message text
// and converts any remaining inline sources to SharePoint links (legacy fallback).
embedSources(response: string): string {
  // Check if response contains any sources using RegEx matching Sources:
  const matchSources = response.match(/Sources:\s*(.*)/s);
  if (!matchSources) return response;
  // Strip the "Sources:" block entirely — sources are now rendered via the dedicated UI
  const result = response.replace(/\n*Sources:\s*(.*)/s, '').trimEnd();
  return result;
}

// Build SharePoint search link for a source filename
buildSourceLink(filename: string): string {
  const clean = this.removeDates(filename);
  const sharepointBaseUrl = 'https://ficonsulting.sharepoint.com/search/Pages/results.aspx?k=';
  return `${sharepointBaseUrl}${encodeURIComponent(clean)}`;
}

// Get display name for a source filename
getSourceDisplayName(filename: string): string {
  return this.removeDates(filename);
}

animateBotTyping(botMessage: string | Promise<string>, sources: string[] = [], confidence?: 'high' | 'medium' | 'low' | 'none', userQuestion?: string, onComplete?: () => void): void {
  const baseTypingSpeed = 30; // milliseconds per word chunk
  const fadeDuration = 500;   // fade-in duration
  const wordsPerChunk = 3;    // number of words per animation chunk
  let chunkIndex = 0;
  let wasStopped = false;
  let words: string[] = [];
  let lastVisibleTime = Date.now();
  let totalTimeAway = 0;

  this.isStopVisible = true;
  this.botIsTyping = true;

  // Add empty bot message initially
  const botMessageIndex = this.chatMessages.push({ sender: 'bot', text: '', sources, confidence, feedback: null, question: userQuestion }) - 1;
  this.cdr.detectChanges();

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      lastVisibleTime = Date.now();
    } else if (document.visibilityState === 'visible') {
      const timeAway = Date.now() - lastVisibleTime;
      totalTimeAway += timeAway;
      
      const chunksTypedWhileAway = Math.floor(totalTimeAway / (baseTypingSpeed * wordsPerChunk));
      const newChunkIndex = Math.min(
        chunkIndex + chunksTypedWhileAway,
        Math.ceil(words.length / wordsPerChunk) - 1
      );

      if (newChunkIndex * wordsPerChunk >= words.length) {
        // Tab regained focus after the message finished "typing" while hidden.
        // Render the full markdown (not the raw joined words) so it displays formatted.
        const rawFull = words.join(' ');
        this.safeParse(rawFull).then((finalHtmlContent) => {
          if (this.chatMessages[botMessageIndex]) {
            this.chatMessages[botMessageIndex].rendered = true;
            this.chatMessages[botMessageIndex].text = this.sanitizer.bypassSecurityTrustHtml(finalHtmlContent);
            this.chatMessages[botMessageIndex].rawText = rawFull;
            this.cdr.detectChanges();
          }
        });
        this.botIsTyping = false;
        this.isStopVisible = false;
        this.cdr.detectChanges();

        if (!wasStopped && this.currentChatId) {
          this.historyService.appendMessage(this.currentChatId, rawFull, 'bot');
        }

        if (onComplete) onComplete();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        return;
      }

      chunkIndex = newChunkIndex;
      processMessage();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  const processMessage = async () => {
    if (!this.botIsTyping || wasStopped) {
      return;
    }

    let resolvedMessage = typeof botMessage === 'string' ? botMessage : await botMessage;
    resolvedMessage = this.embedSources(resolvedMessage);

    if (!words.length) {
      words = resolvedMessage.split(' ');
    }

    const start = chunkIndex * wordsPerChunk;
    const end = start + wordsPerChunk;
    const fullMessage = words.slice(0, end).join(' ');

    const isFinalChunk = end >= words.length;

    // Progressive markdown rendering: parse the text revealed so far through
    // safeParse on every chunk so the user always sees formatted markdown
    // (bold, code blocks, lists) as it "types" — never the raw ** / ``` syntax.
    // marked handles partial input gracefully (an unclosed ``` fence just grows
    // as a code block until it closes on a later chunk).
    if (!this.chatMessages[botMessageIndex].rendered) {
      const progressiveHtml = await this.safeParse(fullMessage);
      // Guard against a later chunk / final render having already won the race.
      if (this.chatMessages[botMessageIndex] && !this.chatMessages[botMessageIndex].rendered) {
        this.chatMessages[botMessageIndex].text = this.sanitizer.bypassSecurityTrustHtml(progressiveHtml);
        this.cdr.detectChanges();
      }
    }

    if (end < words.length) {
      setTimeout(() => {
        chunkIndex++;
        processMessage();
      }, baseTypingSpeed * wordsPerChunk);
    } else {
      // Final rendering after animation. Mark this message as rendered so any
      // in-flight fade loops stop writing to it.
      const finalHtmlContent = await this.safeParse(resolvedMessage);
      this.chatMessages[botMessageIndex].rendered = true;
      this.chatMessages[botMessageIndex].text = this.sanitizer.bypassSecurityTrustHtml(finalHtmlContent);
      this.chatMessages[botMessageIndex].rawText = resolvedMessage;

      if (!wasStopped && this.currentChatId) {
        this.historyService.appendMessage(this.currentChatId, resolvedMessage, 'bot');
      }

      this.isStopVisible = false;
      this.botIsTyping = false;
      this.cdr.detectChanges();

      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (onComplete) onComplete();
      if (this.currentChatId) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { chatId: this.currentChatId },
          queryParamsHandling: 'merge',
        });
      }
    }
  };

  processMessage();
}


/** Apply smooth fade-in effect for the last chunk of words — no safeParse in the loop */
fadeInLastChunk(botMessageIndex: number, fullMessage: string, fadeDuration: number, isFinalChunk: boolean): void {
  const startTime = Date.now();
  const wordArr = fullMessage.split(' ');
  const fadingChunk = wordArr.slice(-3).join(' ');
  const solidText = wordArr.slice(0, -3).join(' ') + (wordArr.length > 3 ? ' ' : '');

  // Skip fade animation inside code blocks — just show the text as-is
  const backtickCount = (solidText.match(/```/g) || []).length;
  const isInsideCodeBlock = backtickCount % 2 === 1;

  if (isInsideCodeBlock) return; // safeParse at the end of processMessage handles code blocks

  const updateFade = () => {
    // If the final markdown render has already run for this message, stop —
    // otherwise we'd clobber parsed HTML with raw escaped text.
    if (!this.chatMessages[botMessageIndex] || this.chatMessages[botMessageIndex].rendered) {
      return;
    }

    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / fadeDuration, 1);
    const opacity = this.easeOut(progress);

    // Render plain text + opacity span — no safeParse, just bypassSecurityTrustHtml
    const html = this.escapeHtmlFade(solidText) +
      `<span style="opacity:${opacity.toFixed(3)}">${this.escapeHtmlFade(fadingChunk)}</span>`;

    this.chatMessages[botMessageIndex].text = this.sanitizer.bypassSecurityTrustHtml(html);
    this.cdr.detectChanges();

    if (progress < 1) {
      requestAnimationFrame(updateFade);
    }
    // Final markdown render is handled by safeParse in processMessage after the animation
  };

  updateFade();
}

/** Lightweight HTML escape for fade animation — no DOM parsing */
private escapeHtmlFade(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}

/** Apply inline opacity to the last chunk */
applyOpacity(word: string, opacity: number): string {
  const opacityValue = Math.round(opacity * 100); // Convert to percentage
  return `<span style="opacity: ${opacityValue / 100}; transition: opacity 0.8s;">${word}</span>`;
}

/** Safely parse markdown and add copy buttons to code blocks */
private async safeParse(markdown: string): Promise<string> {
  const html = await marked.parse(markdown);
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Add copy buttons to all code blocks
  const codeBlocks = doc.querySelectorAll('pre code');
  codeBlocks.forEach((codeBlock) => {
    const pre = codeBlock.parentElement;
    if (pre) {
      // Get the code content and store it
      const codeContent = codeBlock.textContent || '';
      
      // Create a wrapper div for the pre element
      const wrapper = doc.createElement('div');
      wrapper.className = 'code-block-wrapper';
      
      // Create the copy button
      const copyButton = doc.createElement('button');
      copyButton.className = 'copy-button';
      copyButton.setAttribute('data-code', this.escapeHtml(codeContent));
      copyButton.onclick = (event) => this.handleCopyClick(event as MouseEvent);
      
      // Create and add the copy icon
      const copyIcon = doc.createElement('img');
      copyIcon.src = 'assets/custom-icons/copy.svg';
      copyIcon.alt = 'Copy';
      copyIcon.className = 'copy-icon';
      copyButton.appendChild(copyIcon);
      
      // Create and add the checkmark icon
      const checkmarkIcon = doc.createElement('img');
      checkmarkIcon.src = 'assets/custom-icons/checkmark.svg';
      checkmarkIcon.alt = 'Copied';
      checkmarkIcon.className = 'checkmark-icon';
      checkmarkIcon.style.display = 'none';
      copyButton.appendChild(checkmarkIcon);
      
      // Insert the wrapper before the pre element
      pre.parentNode?.insertBefore(wrapper, pre);
      
      // Move the pre element into the wrapper
      wrapper.appendChild(pre);
      
      // Add the copy button inside the pre element
      pre.appendChild(copyButton);
    }
  });

  // Wrap tables for mobile responsiveness
  const tables = doc.querySelectorAll('table');
  tables.forEach((table) => {
    // Check if table is already wrapped
    if (table.parentElement?.classList.contains('table-wrapper')) {
      return;
    }
    
    // Create a wrapper div for the table
    const wrapper = doc.createElement('div');
    wrapper.className = 'table-wrapper';
    
    // Insert the wrapper before the table
    table.parentNode?.insertBefore(wrapper, table);
    
    // Move the table into the wrapper
    wrapper.appendChild(table);
  });

  // Shrink literal square-bullet glyphs (■) the backend emits — wrap them in a
  // span we can size down via CSS. Skip anything inside code/pre so code is untouched.
  this.shrinkSquareBullets(doc);

  return doc.body.innerHTML;
}

/** Wrap literal ■ characters in a .sq-bullet span so CSS can render them at a normal size. */
private shrinkSquareBullets(doc: Document): void {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (!node.textContent || node.textContent.indexOf('\u25A0') === -1) {
        return NodeFilter.FILTER_REJECT;
      }
      // Don't touch bullets inside code or pre blocks.
      let el: HTMLElement | null = node.parentElement;
      while (el) {
        const tag = el.tagName?.toLowerCase();
        if (tag === 'code' || tag === 'pre') return NodeFilter.FILTER_REJECT;
        el = el.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    }
  });

  const targets: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    targets.push(current as Text);
    current = walker.nextNode();
  }

  targets.forEach((textNode) => {
    const parts = textNode.textContent!.split('\u25A0');
    const frag = doc.createDocumentFragment();
    parts.forEach((part, i) => {
      if (i > 0) {
        const span = doc.createElement('span');
        span.className = 'sq-bullet';
        span.textContent = '\u25A0';
        frag.appendChild(span);
      }
      if (part) frag.appendChild(doc.createTextNode(part));
    });
    textNode.parentNode?.replaceChild(frag, textNode);
  });
}

/** Escape HTML for use in data attribute */
private escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
    .replace(/\t/g, "\\t");
}

/** Handle copy button click */
public handleCopyClick(event: MouseEvent): void {
  const button = (event.target as HTMLElement).closest('.copy-button');
  if (!button) {
    return;
  }
  
  const code = button.getAttribute('data-code');
  if (!code) {
    return;
  }
  
  const decodedCode = this.unescapeHtml(code);
  
  const textarea = document.createElement('textarea');
  textarea.value = decodedCode;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  
  try {
    textarea.select();
    const successful = document.execCommand('copy');
    
    if (successful) {
      const copyIcon = button.querySelector('.copy-icon') as HTMLElement;
      const checkmarkIcon = button.querySelector('.checkmark-icon') as HTMLElement;
      
      if (copyIcon && checkmarkIcon) {
        copyIcon.style.display = 'none';
        checkmarkIcon.style.display = 'inline';
        
        setTimeout(() => {
          copyIcon.style.display = 'inline';
          checkmarkIcon.style.display = 'none';
        }, 2000);
      }
    }
  } catch (err) {
    // Error handling without console log
  } finally {
    document.body.removeChild(textarea);
  }
}

/** Unescape HTML from data attribute */
private unescapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\r")
    .replace(/\\t/g, "\t");
}

/** Ease-out function for smooth fade-in */
private easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3); // Cubic ease-out for smoother animation
}

  /** Adjusts the height of the chat input field */
  adjustTextareaHeight(): void {
    const textarea = document.querySelector('textarea.input-field') as HTMLTextAreaElement;
    if (textarea) {
      const defaultHeight = 24;
      const maxHeight = 6 * 24;
      textarea.style.height = `${defaultHeight}px`;
      if (textarea.scrollHeight > maxHeight) {
        textarea.style.height = `${maxHeight}px`;
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.height = `${textarea.scrollHeight}px`;
        textarea.style.overflowY = 'hidden';
      }
    }
  }

  /** Resets the chat input field height */
  resetTextareaHeight(): void {
    const textarea = document.querySelector('textarea.input-field') as HTMLTextAreaElement;
    if (textarea) {
      textarea.style.height = '24px';
      textarea.style.overflowY = 'hidden';
    }
  }

  /** Resets the input wrapper height */
  resetInputWrapperHeight(): void {
    const inputWrapper = document.querySelector('.input-wrapper') as HTMLElement;
    if (inputWrapper) {
      inputWrapper.style.height = 'auto';
    }
  }

  /** Scrolls to the latest message */
  private scrollToBottom(): void {
    setTimeout(() => {
      const container = this.messagesContainer.nativeElement;
      container.scroll({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }, 0);
  }

  // logout(): void {
  //   this.cancelBotResponse();
  //   this.localsessionService.clearSession();
  //   window.location.href = '/login';
  // }
  
  /**
   * Mode changed from the Tools menu (only reachable in general mode now).
   *
   * - Settings (context/temperature) just toggle on the current chat.
   * - Selecting a top-level surface (code/writing):
   *     • If the current chat is empty/unsaved, adopt the mode in place.
   *     • If the current chat already has messages, open a NEW chat in that
   *       mode and navigate there, leaving the original chat intact.
   */
  onModeChanged(event: { mode: string, event: Event }): void {
    const requestedAppMode =
      event.mode === 'code' ? 'code' :
      event.mode === 'writing' ? 'writing' :
      event.mode === 'image' ? 'image' : 'standard';

    // Settings-style toggles (context) stay on the current chat.
    if (event.mode === 'context') {
      this.selectedMode = event.mode;
      this.modeService.setMode(event.mode);
      this.context = '';
      this.cdr.detectChanges();
      return;
    }

    const switchingToSurface = requestedAppMode === 'code' || requestedAppMode === 'writing' || requestedAppMode === 'image';
    const currentChatHasContent = !!this.currentChatId && this.chatMessages.length > 0;

    if (switchingToSurface && currentChatHasContent) {
      // Open a brand-new chat in the requested mode — don't mutate the current one.
      this.startNewChatInMode(requestedAppMode);
      return;
    }

    // Empty/unsaved chat: adopt the mode in place.
    this.selectedMode = event.mode;
    this.modeService.setMode(event.mode);
    if (this.currentChatId) {
      this.historyService.setConversationMode(this.currentChatId, requestedAppMode as ConversationMode);
      this.historyService.historyUpdated.next();
    }
    if (requestedAppMode !== 'writing') {
      this.writingMode = 'generate';
    }
    this.cdr.detectChanges();
  }

  /** Create a fresh empty chat already set to the given mode, and navigate to it. */
  private startNewChatInMode(mode: 'standard' | 'code' | 'writing' | 'image'): void {
    this.cancelBotResponse();
    this.resetModeState();
    // Preserve the chosen mode through the chatId:null navigation below.
    this.pendingModeForNewChat = mode;
    this.selectedMode = mode;
    this.modeService.setMode(mode);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { chatId: null },
      queryParamsHandling: 'merge'
    });
    this.cdr.detectChanges();
  }

  /**
   * Hard reset of every piece of mode/input state. Called before loading a
   * chat, on New Chat, and when switching surfaces, so nothing leaks across
   * sessions (stray badges, writing tabs, attachments, drafts).
   */
  private resetModeState(): void {
    this.selectedMode = 'standard';
    this.modeService.setMode('standard');
    this.writingMode = 'generate';
    this.writingHint = '';
    this.context = '';
    this.attachedImage = null;
    this.attachedFile = null;
    this.attachedFileDataUri = null;
    this.flaggedWords = [];
    this.selectedFiles = [];
    this.chatForm?.reset();
  }

  /** Current normalised view mode, for template branching. */
  get appMode(): 'standard' | 'code' | 'writing' | 'image' {
    return this.modeService.getAppMode();
  }

  /** Mode-aware placeholder for the chat input. */
  get chatPlaceholder(): string {
    switch (this.appMode) {
      case 'code':
        return 'Ask anything about code';
      case 'writing':
        return this.writingMode === 'generate'
          ? 'Describe what you want written'
          : 'Add instructions (optional)';
      case 'image':
        return 'Describe the image you want to generate';
      default:
        return 'Ask anything';
    }
  }

  /** Switch the writing sub-mode (review/rewrite/generate). */
  setWritingMode(mode: WritingMode): void {
    this.writingMode = mode;
    this.writingHint = ''; // clear any stale hint when switching tabs
    this.cdr.detectChanges();
  }

  /** Live word count of the chat input (shown in writing mode). */
  get inputWordCount(): number {
    const text = (this.chatForm?.get('message')?.value || '').trim();
    return text ? text.split(/\s+/).length : 0;
  }

  /**
   * Called on input in writing mode. Recomputes the prohibited-word flags so the
   * UI can warn the user before they submit (purely client-side, no API call).
   */
  onWritingInput(): void {
    if (this.modeService.getAppMode() !== 'writing') {
      if (this.flaggedWords.length) this.flaggedWords = [];
      return;
    }
    const text = (this.chatForm?.get('message')?.value || '').toLowerCase();
    const found = new Set<string>();
    for (const word of this.prohibitedWords) {
      // Word-boundary match so "all" doesn't fire inside "small".
      const re = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      if (re.test(text)) found.add(word);
    }
    this.flaggedWords = Array.from(found);
  }

  /**
   * Paste handler for the chat input. If the clipboard carries an image, stage
   * it for the vision endpoint and block the default text paste. Works in all modes.
   */
  onPaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const blob = item.getAsFile();
        if (!blob) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
          this.attachedImage = {
            dataUri: (ev.target?.result as string) ?? '',
            mimetype: item.type
          };
          this.attachedFile = null;
          this.cdr.detectChanges();
        };
        reader.readAsDataURL(blob);
        return;
      }
    }
    // No image — let the normal text paste proceed.
  }

  /** File picker handler (the + button). Images → vision, other files → ingest. */
  onAttachFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.attachedImage = {
          dataUri: (ev.target?.result as string) ?? '',
          mimetype: file.type || 'image/png'
        };
        this.attachedFile = null;
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(file);
    } else {
      this.attachedFile = file;
      this.attachedImage = null;
      // Capture a data URI so the file can be shown + re-downloaded from the chat log.
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.attachedFileDataUri = (ev.target?.result as string) ?? null;
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(file);
      this.cdr.detectChanges();
    }
    input.value = '';
  }

  /** Remove the staged attachment preview. */
  clearAttachment(): void {
    this.attachedImage = null;
    this.attachedFile = null;
    this.attachedFileDataUri = null;
    this.cdr.detectChanges();
  }

  /** Download a stored file attachment from the chat log. */
  downloadFileAttachment(att: { name: string; dataUri: string }): void {
    const a = document.createElement('a');
    a.href = att.dataUri;
    a.download = att.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /**
   * Copy a bot message as clean plain text (markdown stripped) so it pastes
   * cleanly into Word. Uses the stored rawText when available.
   */
  copyAsCleanText(msgIndex: number): void {
    const msg = this.chatMessages[msgIndex];
    if (!msg) return;
    const raw = msg.rawText ?? '';
    const clean = raw
      .replace(/```[\s\S]*?```/g, (m) => m.replace(/```[a-z]*\n?/gi, '').replace(/```/g, '')) // code fences
      .replace(/`([^`]+)`/g, '$1')          // inline code
      .replace(/\*\*([^*]+)\*\*/g, '$1')    // bold
      .replace(/\*([^*]+)\*/g, '$1')        // italic
      .replace(/^#{1,6}\s+/gm, '')          // headings
      .replace(/^\s*[-*]\s+/gm, '• ')       // bullets
      .trim();
    navigator.clipboard?.writeText(clean).catch(err => console.error('Copy failed:', err));
  }

  /** Copy a chart image (data URI) to the clipboard as a PNG blob. */
  async copyChart(dataUri: string): Promise<void> {
    try {
      const blob = await (await fetch(dataUri)).blob();
      // ClipboardItem may not exist in older browsers.
      if (typeof (window as any).ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        const item = new (window as any).ClipboardItem({ [blob.type]: blob });
        await navigator.clipboard.write([item]);
      } else {
        // Fallback: copy the data URI as text.
        await navigator.clipboard?.writeText(dataUri);
      }
    } catch (err) {
      console.error('Chart copy failed:', err);
    }
  }

  /** Download a chart image (data URI) as a PNG file. */
  downloadChart(dataUri: string): void {
    const a = document.createElement('a');
    a.href = dataUri;
    a.download = `figpt-chart-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1); // Remove specific file
  }

  /** Handle clicks on message content */
  public onMessageClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const copyButton = target.closest('.copy-button');
    
    if (copyButton) {
      this.handleCopyClick(event);
    }
  }
}